import { useCallback, useContext, useEffect } from 'react';
import { SettingsContext, UserContext } from '@/state/contexts';
import { useLogoutMutation } from '@/state/slices/auth';
import type { SessionDuration } from '@/models/settings';
import { performClientLogout } from '@/state/utils/authSession';

const DURATION_MS: Record<SessionDuration, number | null> = {
  '24_HOURS': 86_400_000,
  '48_HOURS': 172_800_000,
  '1_WEEK': 604_800_000,
  '1_MONTH': 2_592_000_000,
  OFF: null,
};

const CHECK_INTERVAL_MS = 60_000;

export function SessionDurationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, setUser } = useContext(UserContext);
  const { settings } = useContext(SettingsContext);
  const [logoutFn] = useLogoutMutation();

  const isAuthenticated = !!user?.token;
  const durationMs = DURATION_MS[settings.sessionDuration] ?? null;
  const isActive = isAuthenticated && durationMs !== null;

  const forceLogout = useCallback(() => {
    performClientLogout(logoutFn, setUser);
    window.location.assign('/login');
  }, [logoutFn, setUser]);

  useEffect(() => {
    if (!isActive) return undefined;

    const checkSession = () => {
      const sessionStart = Number(localStorage.getItem('sessionStart'));
      if (sessionStart && Date.now() >= sessionStart + durationMs) {
        forceLogout();
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isActive, durationMs, forceLogout]);

  return <>{children}</>;
}
